import SuperTokens from "supertokens-web-js";
import type { StorageHandler } from "supertokens-web-js/lib/build/windowHandler/types";
import EmailPassword from "supertokens-web-js/recipe/emailpassword";
import EmailVerification from "supertokens-web-js/recipe/emailverification";
import OAuth2Provider from "supertokens-web-js/recipe/oauth2provider";
import Passwordless from "supertokens-web-js/recipe/passwordless";
import Session from "supertokens-web-js/recipe/session";

type SessionEvent = Parameters<NonNullable<Parameters<typeof Session.init>[0]>["onHandleEvent"] & {}>[0];
type SessionEventHandler = (event: SessionEvent) => void;

// SuperTokens should only be initialized once. Even complete re-rendering of the app should not re-initialize it.
let initializedAt: Date | undefined;
let eventHandler: SessionEventHandler | undefined;
const pendingEvents: SessionEvent[] = [];

/**
 * Initializes the SDK once. Router loaders call this before React mounts the session provider, so session
 * events can arrive before a handler exists; they are queued until `setSessionEventHandler` is called.
 */
export function initializeSuperTokens(urls: { authenticationServerRootUrl: string; newCoreApiRootUri: string }): void {
  if (initializedAt) {
    return;
  }

  SuperTokens.init({
    appInfo: {
      appName: "Area of People",
      apiDomain: urls.authenticationServerRootUrl,
    },
    windowHandler: (original) => {
      return {
        ...original,
        // Override localstorage usage within supertokensso we don't have
        // localStorage edgecase crashes if localStorage is null. (can happen when safari private mode is enabled, or user has disabled localStorage)
        // https://github.com/supertokens/supertokens-website/issues/232#issuecomment-1719213092
        localStorage: createLocalStorageFallback(),
      };
    },
    recipeList: [
      EmailPassword.init(),
      EmailVerification.init(),
      Session.init({
        maxRetryAttemptsForSessionRefresh: 1,
        tokenTransferMethod: "header",
        sessionTokenBackendDomain: urls.newCoreApiRootUri,
        onHandleEvent(event) {
          if (eventHandler) {
            eventHandler(event);
          } else {
            pendingEvents.push(event);
          }
        },
      }),
      Passwordless.init(),
      OAuth2Provider.init(),
    ],
  });

  initializedAt = new Date();
}

export function getSuperTokensInitializedAt(): Date | undefined {
  return initializedAt;
}

/** Routes session events to React. Replays events that arrived before the handler was registered. */
export function setSessionEventHandler(handler: SessionEventHandler): () => void {
  eventHandler = handler;
  for (const event of pendingEvents.splice(0)) {
    handler(event);
  }

  return () => {
    if (eventHandler === handler) {
      eventHandler = undefined;
    }
  };
}

function createLocalStorageFallback() {
  // In-memory fallback (per-tab)
  const memoryStore = new Map();

  const storageAvailable = (() => {
    try {
      if (!window.localStorage) return false;
      const test = "__st_test__";
      window.localStorage.setItem(test, test);
      window.localStorage.removeItem(test);

      return true;
    } catch {
      return false;
    }
  })();

  return {
    // oxlint-disable-next-line typescript/require-await
    clear: async () => {
      if (storageAvailable) {
        window.localStorage.clear();
      } else {
        memoryStore.clear();
      }
    },
    clearSync: () => {
      if (storageAvailable) {
        window.localStorage.clear();
      } else {
        memoryStore.clear();
      }
    },
    // oxlint-disable-next-line typescript/require-await
    key: (async (index) => {
      if (storageAvailable) {
        return window.localStorage.key(index);
      }

      return Array.from(memoryStore.keys())[index] ?? null;
    }) as StorageHandler["key"],
    keySync: ((index) => {
      if (storageAvailable) {
        return window.localStorage.key(index);
      }

      return Array.from(memoryStore.keys())[index] ?? null;
    }) as StorageHandler["keySync"],
    // oxlint-disable-next-line typescript/require-await
    setItem: (async (key, value) => {
      if (storageAvailable) {
        window.localStorage.setItem(key, value);
      } else {
        memoryStore.set(key, value);
      }
    }) as StorageHandler["setItem"],
    setItemSync: ((key, value) => {
      if (storageAvailable) {
        window.localStorage.setItem(key, value);
      } else {
        memoryStore.set(key, value);
      }
    }) as StorageHandler["setItemSync"],
    // oxlint-disable-next-line typescript/require-await
    getItem: (async (key) => {
      if (storageAvailable) {
        return window.localStorage.getItem(key);
      }

      return memoryStore.has(key) ? memoryStore.get(key) : null;
    }) as StorageHandler["getItem"],
    getItemSync: ((key) => {
      if (storageAvailable) {
        return window.localStorage.getItem(key);
      }

      return memoryStore.has(key) ? memoryStore.get(key) : null;
    }) as StorageHandler["getItemSync"],
    // oxlint-disable-next-line typescript/require-await
    removeItem: (async (key) => {
      if (storageAvailable) {
        window.localStorage.removeItem(key);
      } else {
        memoryStore.delete(key);
      }
    }) as StorageHandler["removeItem"],
    removeItemSync: ((key) => {
      if (storageAvailable) {
        window.localStorage.removeItem(key);
      } else {
        memoryStore.delete(key);
      }
    }) as StorageHandler["removeItemSync"],
  };
}
